import { TUNABLES } from "../config";
import type { FactionId } from "../core";
import { phrases } from "../i18n";
import { type RoomSides, seatsOf } from "../net/modes";
import { shownName } from "../net/names";
import { type PublicGame, publicGame, type RoomMode, type SeatedPlayer } from "../net/protocol";

/**
 * The front page's games (B3.2, docs/PLAYTEST-PROTOTYPE-PLAN.md, screen 1). A source says which
 * games the page lists at a moment; `lobbyCards.ts` draws them. The local source, in every
 * build, lists only what is true without a server: games against scripts that start when the
 * visitor joins (K2: the duels and a company duel; K6.1: two against two too). The mock
 * (`mockLobby.ts`) imitates a server's public lobby in dev and e2e builds only; the lobby
 * server's is `serverLobby.ts` (B3.5). Pure, so it is unit-tested in Node.
 */

/**
 * A game on the page's list: the lobby server's own shape (its ID a code of `net/roomCode.ts`,
 * which is its seed text), with `startsAt` on the source's clock, null while it waits for
 * someone to join.
 */
export type LobbyGame = PublicGame;

export interface LobbySource {
  /** Which source it is, for the page's `data-lobby` and the tests. */
  readonly kind: string;
  /** The words over the list, in the language of the moment. */
  readonly title: string;
  games(now: number): readonly LobbyGame[];
  /** D-120: false while the source's first word is still coming (the lobby server's); absent, it has it. */
  readonly settled?: boolean;
}

/** A game of the page's own sources, with its mode's seats; a duel unless named. */
export function duel(
  id: string,
  humans: number,
  startsAt: number | null,
  mode: RoomMode = "duel",
): LobbyGame {
  return publicGame(mode, id, seatsOf(mode), humans, startsAt);
}

/** The playtest URL's list before there is a server: games that start when the visitor joins. */
export function createLocalLobby(codeOf: (index: number) => string): LobbySource {
  const { listed } = TUNABLES.lobby;
  const modes: readonly RoomMode[] = [
    ...Array<RoomMode>(listed.duel).fill("duel"),
    ...Array<RoomMode>(listed.company).fill("company"),
    ...Array<RoomMode>(listed.allies).fill("allies"),
    ...Array<RoomMode>(listed.war).fill("war"),
  ];
  const games = modes.map((mode, index) => duel(codeOf(index), 0, null, mode));
  return {
    kind: "local",
    get title() {
      return phrases().front.lobby.duels;
    },
    games: () => games,
  };
}

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/**
 * A card's two lines. Nobody seated and no clock: it starts when you join, and the card says
 * that instead of "0 / 2" (an empty counter reads as an empty game). Someone seated: the seats
 * and the countdown in whole seconds, then "Starting…" at zero.
 */
export function cardText(game: LobbyGame, now: number): { seats: string; clock: string } {
  const words = phrases().front.lobby;
  if (game.startsAt === null) {
    return { seats: words.startsWhenYouJoin, clock: "" };
  }
  const seats = words.seated(game.humans, game.seats);
  const remaining = game.startsAt - now;
  if (remaining <= 0) {
    return { seats, clock: words.starting };
  }
  const total = Math.ceil(remaining / MS_PER_SECOND);
  const minutes = Math.floor(total / SECONDS_PER_MINUTE);
  const seconds = String(total % SECONDS_PER_MINUTE).padStart(2, "0");
  return { seats, clock: `${minutes}:${seconds}` };
}

/** K7.1: a side of up to this many kingdoms names each in the seated line, a scripted one "script". */
const SIDE_NAMED_KINGDOMS = 2;

/**
 * The table a seated visitor sees on the card: each kingdom's humans, or "script" for a kingdom
 * none sits in, kingdom against kingdom ("Anna vs script"; K2: "Anna, Boris vs Carla"). K4.1: at a
 * table of several, how many asked to start now ("· 1 of 2 ready"); alone, the clock says it.
 * K6.1: a table of sides reads side against side, its kingdoms one after another
 * ("Anna, script vs Carla, script"). K7.1: a side of more kingdoms than that names its humans and
 * counts its scripts ("Anna, Boris, 8 scripts vs 10 scripts").
 */
export function tableNote(table: readonly SeatedPlayer[], ready = 0, sides?: RoomSides): string {
  const words = phrases().front.lobby;
  const namesIn = (faction: FactionId) =>
    table
      .filter((seat) => seat.faction === faction && seat.name !== null)
      .map((seat) => shownName(seat.name ?? "", phrases().common.guest));
  const kingdom = (faction: FactionId) => {
    const names = namesIn(faction);
    return names.length > 0 ? names.join(", ") : words.script;
  };
  const sideNote = (side: readonly FactionId[]) => {
    if (side.length <= SIDE_NAMED_KINGDOMS) {
      return side.map(kingdom).join(", ");
    }
    const scripted = side.filter((faction) => namesIn(faction).length === 0).length;
    return [...side.flatMap(namesIn), ...(scripted > 0 ? [words.scripts(scripted)] : [])].join(
      ", ",
    );
  };
  const kingdoms = [...new Set(table.map((seat) => seat.faction))];
  const versus = (sides ?? kingdoms.map((faction) => [faction])).map(sideNote).join(" vs ");
  const humans = table.filter((seat) => seat.name !== null).length;
  return humans > 1 && ready > 0 ? words.ready(versus, ready, humans) : versus;
}
